import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  TextField,
  Grid,
  Alert,
  CircularProgress,
  MenuItem
} from '@mui/material';
import { Save, Cancel } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { principalAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { useFormValidation } from '@/hooks/useFormValidation';
import { required } from '@/utils/validators';

const schema = {
  name: [required('Class name')],
};

const AddClass = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { id } = useParams();
  const isEditMode = Boolean(id);
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [teachers, setTeachers] = useState([]);
  const [fetchingClass, setFetchingClass] = useState(isEditMode);

  const [formData, setFormData] = useState({
    name: '',
    grade: '',
    section: '',
    teacher_id: ''
  });

  const { handleBlur, validateAll, applyServerErrors, showError } = useFormValidation(schema);

  useEffect(() => {
    const init = async () => {
      await fetchTeachers();
      if (isEditMode) {
        try {
          const response = await principalAPI.getClasses();
          const classData = response.data.classes.find(c => c.id === id);
          if (classData) {
            setFormData({
              name: classData.name || '',
              grade: classData.grade || '',
              section: classData.section || '',
              teacher_id: classData.teacher_id || ''
            });
          } else {
            setError('Class not found');
          }
        } catch (error) {
          setError('Failed to load class data');
        } finally {
          setFetchingClass(false);
        }
      }
    };

    init();
  }, [id, isEditMode]);

  const fetchTeachers = async () => {
    try {
      const response = await principalAPI.getTeachers();
      console.log('Teachers response:', response.data);
      const teachersList = response.data.teachers || [];
      console.log('Teachers list:', teachersList);
      setTeachers(teachersList);
    } catch (error) {
      console.error('Error fetching teachers:', error);
      setTeachers([]);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateAll(formData)) return;
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      // Clean up data
      const cleanedData = {
        ...formData,
        grade: formData.grade || null,
        teacher_id: formData.teacher_id || null
      };

      if (isEditMode) {
        await principalAPI.updateClass(id, cleanedData);
        setSuccess('Class updated successfully!');
      } else {
        await principalAPI.createClass(cleanedData);
        setSuccess('Class created successfully!');
      }
      
      // Navigate back to classes list after 2 seconds
      setTimeout(() => {
        navigate('/principal/classes');
      }, 2000);

    } catch (error) {
      const serverErrors = error.response?.data?.errors;
      if (serverErrors) {
        applyServerErrors(serverErrors);
      } else {
        setError(error.response?.data?.message || `Failed to ${isEditMode ? 'update' : 'create'} class`);
      }
    } finally {
      setLoading(false);
    }
  };

  if (fetchingClass) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: { xs: 2, md: 4 }, mb: 4, px: { xs: 1.5, sm: 3 } }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Typography variant="h5" fontWeight="bold" gutterBottom>
          {isEditMode ? 'Edit Class' : 'Add New Class'}
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 3 }}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                label="Class Name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                onBlur={(e) => handleBlur('name', e.target.value)}
                error={Boolean(showError('name'))}
                placeholder="e.g., Class 10-A"
                helperText={showError('name') || 'Full name of the class'}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Grade/Section"
                name="grade"
                value={formData.grade}
                onChange={handleChange}
                placeholder="e.g., 10"
                helperText="Optional: Grade/Section level (also called Standard or Class in some schools)"
                inputProps={{ maxLength: 20 }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Section"
                name="section"
                value={formData.section}
                onChange={handleChange}
                placeholder="e.g., A"
                helperText="Optional: Section identifier (A, B, C, etc.)"
                inputProps={{ maxLength: 5 }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                select
                label="Class Teacher"
                name="teacher_id"
                value={formData.teacher_id}
                onChange={handleChange}
                helperText="Optional: Assign a class teacher"
              >
                <MenuItem value="">None</MenuItem>
                {teachers.map((teacher) => (
                  <MenuItem key={teacher.id} value={teacher.id}>
                    {teacher.user?.name || teacher.employee_id || 'Unknown Teacher'}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12}>
              <Box display="flex" gap={2} justifyContent="flex-end">
                <Button
                  variant="outlined"
                  startIcon={<Cancel />}
                  onClick={() => navigate('/principal/classes')}
                  disabled={loading}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  startIcon={<Save />}
                  disabled={loading}
                >
                  {loading ? <CircularProgress size={24} /> : (isEditMode ? 'Update Class' : 'Create Class')}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Paper>
    </Container>
  );
};

export default AddClass;
